import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import './App.css';
import UploadImage from './components/UploadImage';
import ColorPicker from './components/ColorPicker';
import DeviceSelector from './components/DeviceSelector';
import AccordionSection from './components/AccordionSection';
import PreviewCanvas from './components/PreviewCanvas';
import BrandLogo from './components/BrandLogo';
import DEVICE_CONFIG from '@shared/config';
import { buildApiUrl } from './utils/api';

const DRAFT_KEY = 'screenshot-studio-draft';
const PRESET_KEY = 'app-visual-generator-presets';
const createId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const cloneDraft = (value) => typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value));
const GRADIENTS = [
  { name: 'Ocean', primary: '#2563EB', secondary: '#06B6D4' },
  { name: 'Aurora', primary: '#7C3AED', secondary: '#EC4899' },
  { name: 'Sunset', primary: '#F97316', secondary: '#DB2777' },
  { name: 'Forest', primary: '#059669', secondary: '#84CC16' },
  { name: 'Midnight', primary: '#0F172A', secondary: '#334155' },
  { name: 'Fire', primary: '#DC2626', secondary: '#F59E0B' },
  { name: 'Neon', primary: '#00F5A0', secondary: '#00D9F5' },
  { name: 'Candy', primary: '#F9A8D4', secondary: '#C084FC' },
  { name: 'Royal', primary: '#312E81', secondary: '#7C3AED' },
  { name: 'Peach', primary: '#FB7185', secondary: '#FDBA74' },
  { name: 'Skyline', primary: '#0EA5E9', secondary: '#6366F1' },
  { name: 'Lime', primary: '#65A30D', secondary: '#FACC15' },
  { name: 'Berry', primary: '#9D174D', secondary: '#7E22CE' },
  { name: 'Coffee', primary: '#451A03', secondary: '#D97706' },
  { name: 'Ice', primary: '#E0F2FE', secondary: '#38BDF8' },
  { name: 'Cyber', primary: '#111827', secondary: '#06B6D4' },
  { name: 'Flamingo', primary: '#F43F5E', secondary: '#F0ABFC' },
  { name: 'Deep Space', primary: '#020617', secondary: '#1E3A8A' },
  { name: 'Mint', primary: '#14B8A6', secondary: '#BEF264' },
  { name: 'Golden Hour', primary: '#F59E0B', secondary: '#FDE68A' },
];
const TEMPLATES = [
  {
    id: 'top',
    name: 'Top heading',
    description: 'Headline above device',
    headingY: 14,
    subheadingY: 21,
  },
  {
    id: 'side',
    name: 'Side-by-side',
    description: 'Copy aligned to the side',
    headingY: 35,
    subheadingY: 44,
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Balanced, clean canvas',
    headingY: 17,
    subheadingY: 25,
  },
];
const FONT_OPTIONS = [
  'Inter',
  'Roboto',
  'Poppins',
  'Montserrat',
  'DM Sans',
  'Open Sans',
  'Lato',
  'Nunito',
  'Raleway',
  'Oswald',
  'Merriweather',
  'Playfair Display',
  'Manrope',
  'Outfit',
  'Rubik',
  'Space Grotesk',
  'Plus Jakarta Sans',
  'Work Sans',
  'Bebas Neue',
  'Source Sans 3',
];
const EXPORT_PRESET_BY_DEVICE = {
  iphone: 'iphone_1284x2778',
  ipad: 'ipad_2064x2752',
  android: 'store',
};

const defaultDraft = {
  primary: '#2563EB',
  secondary: '#06B6D4',
  frameColor: 'auto',
  devices: ['iphone'],
  heading: 'Build something brilliant',
  subheading: 'A beautiful experience, ready for every screen.',
  headingSize: 5,
  subheadingSize: 3,
  textColor: '#FFFFFF',
  font: 'Inter',
  layout: 'top',
  shadow: 'shadow',
  overlays: [],
  exportPreset: 'store',
  cropFit: 'contain',
  imageZoom: 1,
  imagePosition: { x: 50, y: 50 },
  imageRotation: 0,
  framePosition: { x: 7, y: 24 },
  frameSize: { width: 86, height: 76 },
  textPositions: { heading: { x: 50, y: 14 }, subheading: { x: 50, y: 21 } },
};

function App() {
  const [draft, setDraft] = useState(() => ({
    ...defaultDraft,
    ...JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}'),
  }));
  const [imageState, setImageState] = useState(null);
  const [batchImages, setBatchImages] = useState([]);
  const [activeImageId, setActiveImageId] = useState(null);
  const [batchOutputs, setBatchOutputs] = useState({});
  const [imageDrawerOpen, setImageDrawerOpen] = useState(false);
  const [presets, setPresets] = useState(() => JSON.parse(localStorage.getItem(PRESET_KEY) || '[]'));
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [presetDialogMode, setPresetDialogMode] = useState('create');
  const [presetDeleteArmed, setPresetDeleteArmed] = useState(false);
  const [outputs, setOutputs] = useState({});
  const [activeDevice, setActiveDevice] = useState('iphone');
  const [editingPreview, setEditingPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openSection, setOpenSection] = useState('device');
  const [dark, setDark] = useState(
    () => localStorage.getItem('screenshot-studio-theme') === 'dark',
  );
  const [zoom, setZoom] = useState(1);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const image = imageState;
  const updateDraft = (patch) => {
    setDraft((current) => {
      const next = { ...current, ...patch };
      if (JSON.stringify(current) !== JSON.stringify(next)) {
        setHistory((items) => [...items.slice(-24), current]);
        setFuture([]);
      }
      return next;
    });
  };
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [draft]);
  useEffect(() => {
    localStorage.setItem(PRESET_KEY, JSON.stringify(presets));
  }, [presets]);
  useEffect(() => {
    localStorage.setItem('screenshot-studio-theme', dark ? 'dark' : 'light');
  }, [dark]);
  useEffect(() => {
    if (!image) return;
    setZoom(1);
  }, [image]);
  const selectedLabel = useMemo(
    () =>
      draft.devices
        .map((key) => DEVICE_CONFIG[key]?.label)
        .filter(Boolean)
        .join(', '),
    [draft.devices],
  );
  const chooseTemplate = (template) =>
    updateDraft({
      layout: template.id,
      textPositions: {
        heading: { x: 50, y: template.headingY },
        subheading: { x: 50, y: template.subheadingY },
      },
    });
  const resetImageEditor = () => updateDraft({ framePosition: { x: 7, y: 24 }, frameSize: { width: 86, height: 76 }, cropFit: 'contain', imageZoom: 1, imagePosition: { x: 50, y: 50 }, imageRotation: 0 });
  const selectBatchImage = (id) => {
    const selected = batchImages.find((item) => item.id === id);
    if (!selected) return;
    setActiveImageId(id);
    setImageState(selected.file);
    setOutputs(batchOutputs[id] || {});
    setEditingPreview(!batchOutputs[id]);
    setImageDrawerOpen(false);
  };
  const savePreset = () => {
    if (!presetName.trim()) return;
    setPresets((current) => presetDialogMode === 'edit' ? current.map((preset) => preset.id === selectedPresetId ? { ...preset, name: presetName.trim(), draft: cloneDraft(draft) } : preset) : [...current, { id: createId(), name: presetName.trim(), draft: cloneDraft(draft) }]);
    setPresetName('');
    setPresetDialogOpen(false);
  };
  const applyPreset = (event) => {
    const preset = presets.find((item) => item.id === event.target.value);
    if (preset) { setSelectedPresetId(preset.id); setPresetDeleteArmed(false); setDraft(cloneDraft(preset.draft)); }
  };
  const openCreatePreset = () => { setPresetDialogMode('create'); setPresetName(''); setPresetDialogOpen(true); };
  const openEditPreset = () => { const preset = presets.find((item) => item.id === selectedPresetId); if (!preset) return; setPresetDialogMode('edit'); setPresetName(preset.name); setPresetDialogOpen(true); };
  const deletePreset = () => { if (!selectedPresetId || !presetDeleteArmed) return; setPresets((current) => current.filter((preset) => preset.id !== selectedPresetId)); setSelectedPresetId(''); setPresetDeleteArmed(false); };
  const setImage = (value) => {
    if (Array.isArray(value)) {
      addBatchImages(value);
      return;
    }
    const selected = batchImages.find((item) => item.file === value);
    if (selected) {
      setActiveImageId(selected.id);
      setOutputs(batchOutputs[selected.id] || {});
      setEditingPreview(!batchOutputs[selected.id]);
    }
    setImageState(value);
  };
  const removeImage = (id) => {
    const nextBatchImages = batchImages.filter((item) => item.id !== id);
    const nextBatchOutputs = Object.fromEntries(
      Object.entries(batchOutputs).filter(([imageId]) => imageId !== id),
    );

    if (!nextBatchImages.length) {
      setBatchImages([]);
      setActiveImageId(null);
      setImageState(null);
      setBatchOutputs({});
      setOutputs({});
      setEditingPreview(false);
      return;
    }

    const wasActive = activeImageId === id;
    const nextActiveImage = wasActive
      ? nextBatchImages[0]
      : nextBatchImages.find((item) => item.id === activeImageId) || nextBatchImages[0];

    setBatchImages(nextBatchImages);
    setBatchOutputs(nextBatchOutputs);
    setActiveImageId(nextActiveImage.id);
    setImageState(nextActiveImage.file);
    setOutputs(nextBatchOutputs[nextActiveImage.id] || {});
    setEditingPreview(!nextBatchOutputs[nextActiveImage.id]);
  };
  const addBatchImages = (files) => {
    if (!files.length) return;
    const nextImages = files.map((file) => ({ id: `${file.name}-${file.lastModified}-${createId()}`, file, previewUrl: URL.createObjectURL(file) }));
    setBatchImages((current) => [...current, ...nextImages]);
    setBatchOutputs({});
    setOutputs({});
    setEditingPreview(true);
    selectBatchImage(nextImages[0].id);
    setActiveImageId(nextImages[0].id);
    setImageState(nextImages[0].file);
    resetImageEditor();
  };
  const generate = async () => {
    if (!image) return window.alert('Please upload an image first.');
    if (!draft.devices.length) return window.alert('Select at least one device.');
    setLoading(true);
    try {
      const imagesToGenerate = batchImages.length ? batchImages : [{ id: 'single', file: image }];
      const results = await Promise.all(
        imagesToGenerate.map(async ({ id, file }) => {
          const deviceResults = await Promise.all(draft.devices.map(async (device) => {
          const formData = new FormData();
          formData.append('image', file);
          Object.entries({
            primaryColor: draft.primary,
            secondaryColor: draft.secondary,
            frameColor: draft.frameColor || 'auto',
            device,
            heading: draft.heading,
            subheading: draft.subheading,
            headingSize: draft.headingSize || 5,
            subheadingSize: draft.subheadingSize || 3,
            textColor: draft.textColor || '#FFFFFF',
            font: draft.font,
            layout: draft.layout,
            shadow: draft.shadow,
            overlays: JSON.stringify(draft.overlays),
            textPositions: JSON.stringify(draft.textPositions),
            cropFit: draft.cropFit,
            imageZoom: draft.imageZoom,
            imagePosition: JSON.stringify(draft.imagePosition),
            imageRotation: draft.imageRotation,
            framePosition: JSON.stringify(draft.framePosition),
            frameSize: JSON.stringify(draft.frameSize),
            exportSize: draft.exportPreset,
          }).forEach(([key, value]) => formData.append(key, value));
          const res = await axios.post(buildApiUrl('/generate'), formData, { withCredentials: true });
          return [device, res.data.imageUrl];
          }));
          return [id, Object.fromEntries(deviceResults)];
        }),
      );
      const nextBatchOutputs = Object.fromEntries(results);
      setBatchOutputs(nextBatchOutputs);
      setOutputs(nextBatchOutputs[activeImageId] || nextBatchOutputs.single || {});
      setActiveDevice(draft.devices[0]);
      setEditingPreview(false);
    } catch (error) {
      console.error(error);
      window.alert(
        'Could not generate the preview. Please ensure the backend is running.',
      );
    } finally {
      setLoading(false);
    }
  };
  const download = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  };
  const batchDownload = async () => {
    const zip = new JSZip();
    Object.entries(batchOutputs).forEach(([imageId, imageOutputs]) => {
      const source = batchImages.find((item) => item.id === imageId);
      const baseName = source?.file.name.replace(/\.[^.]+$/, '') || imageId;
      Object.entries(imageOutputs).forEach(([device, url]) => zip.file(`${baseName}-${device}.png`, url.split(',')[1], { base64: true }));
    });
    if (!Object.keys(batchOutputs).length) Object.entries(outputs).forEach(([device, url]) => zip.file(`app-preview-${device}.png`, url.split(',')[1], { base64: true }));
    const blob = await zip.generateAsync({ type: 'blob' });
    download(URL.createObjectURL(blob), 'app-visual-generator-batch.zip');
  };
  const undo = () => {
    const last = history.at(-1);
    if (!last) return;
    setFuture((items) => [draft, ...items]);
    setHistory((items) => items.slice(0, -1));
    setDraft(last);
  };
  const redo = () => {
    const next = future[0];
    if (!next) return;
    setHistory((items) => [...items, draft]);
    setFuture((items) => items.slice(1));
    setDraft(next);
  };
  return (
    <div className={`studio ${dark ? 'dark' : ''}`}>
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark"><BrandLogo /></div>
          <div>
            <strong>App Visual Generator</strong>
            <span>App-store creative workspace</span>
          </div>
        </div>
        <div className="header-actions">
          <button type="button" className="icon-button" onClick={() => setImageDrawerOpen((open) => !open)} title="Uploaded images"><i className="bi bi-list" /></button>
          <button
            type="button"
            className="icon-button"
            disabled={!history.length}
            onClick={undo}
            title="Undo"
          >
            <i className="bi bi-arrow-counterclockwise" />
          </button>
          <button
            type="button"
            className="icon-button"
            disabled={!future.length}
            onClick={redo}
            title="Redo"
          >
            <i className="bi bi-arrow-clockwise" />
          </button>
          <button type="button" className="theme-toggle" onClick={() => setDark(!dark)}>
            <i className={`bi bi-${dark ? 'sun' : 'moon-stars'}`} />
            {dark ? 'Light' : 'Dark'}
          </button>
        </div>
      </header>
      <aside className={`image-drawer ${imageDrawerOpen ? 'open' : ''}`} aria-label="Uploaded images">
        <div className="image-drawer-header"><strong>Uploaded images</strong><button type="button" className="icon-button" onClick={() => setImageDrawerOpen(false)}><i className="bi bi-x-lg" /></button></div>
        {batchImages.length ? <div className="drawer-image-list">{batchImages.map((item, index) => <div key={item.id} className={`drawer-image-card ${item.id === activeImageId ? 'selected' : ''}`}><button type="button" className="drawer-image-select" onClick={() => selectBatchImage(item.id)}><img src={item.previewUrl} alt={item.file.name} /><span>{index + 1}. {item.file.name}</span>{batchOutputs[item.id] && <i className="bi bi-check-circle-fill" />}</button><button type="button" className="drawer-image-remove" onClick={(event) => { event.stopPropagation(); removeImage(item.id); }} title={`Remove ${item.file.name}`}><i className="bi bi-trash3" /></button></div>)}</div> : <p className="hint">Upload one or more images to start a batch.</p>}
      </aside>
      {presetDialogOpen && <div className="preset-modal-backdrop" role="presentation" onMouseDown={() => setPresetDialogOpen(false)}><section className="preset-modal" role="dialog" aria-modal="true" aria-labelledby="preset-dialog-title" onMouseDown={(event) => event.stopPropagation()}><div className="preset-modal-icon"><i className={`bi bi-${presetDialogMode === 'edit' ? 'pencil-square' : 'bookmark-star-fill'}`} /></div><h2 id="preset-dialog-title">{presetDialogMode === 'edit' ? 'Update design preset' : 'Save design preset'}</h2><p>{presetDialogMode === 'edit' ? 'Your current design settings will replace this preset.' : 'Give this design setup a memorable name so you can reuse it on any uploaded image.'}</p><label className="field-label" htmlFor="preset-name">Preset name</label><input id="preset-name" className="studio-input" autoFocus value={presetName} placeholder="e.g. Launch screen blue" onChange={(event) => setPresetName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') savePreset(); }} /><div className="preset-modal-actions"><button type="button" className="secondary-button" onClick={() => setPresetDialogOpen(false)}>Cancel</button><button type="button" className="primary-button" disabled={!presetName.trim()} onClick={savePreset}>{presetDialogMode === 'edit' ? 'Update preset' : 'Save preset'}</button></div></section></div>}
      <main className="workspace">
        <aside className="sidebar">
          <div className="sidebar-title">
            <p>DESIGN CONTROLS</p>
            <span>{selectedLabel || 'No device selected'}</span>
          </div>
          <AccordionSection title="Presets" icon="bi-bookmark-star" isOpen={openSection === 'preset'} onToggle={() => setOpenSection(openSection === 'preset' ? '' : 'preset')}>
            <label className="field-label">Saved design preset</label>
            <select className="studio-input" value={selectedPresetId} onChange={applyPreset}><option value="" disabled>Select a preset</option>{presets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}</select>
            <div className="preset-actions"><button type="button" className="rotate-button" onClick={openCreatePreset}><i className="bi bi-bookmark-plus" /> Save as new preset</button>{selectedPresetId && <><button type="button" className="rotate-button" onClick={openEditPreset}><i className="bi bi-pencil-square" /> Update selected preset</button><label className="preset-delete-check"><input type="checkbox" checked={presetDeleteArmed} onChange={(event) => setPresetDeleteArmed(event.target.checked)} /> I want to delete this preset</label><button type="button" className="delete-preset-button" disabled={!presetDeleteArmed} onClick={deletePreset}><i className="bi bi-trash3" /> Delete selected preset</button></>}</div>
          </AccordionSection>
          <AccordionSection
            title="Devices"
            icon="bi-phone"
            isOpen={openSection === 'device'}
            onToggle={() => setOpenSection(openSection === 'device' ? '' : 'device')}
          >
            <DeviceSelector
              devices={draft.devices}
              onChange={(devices) => {
                const device = devices[0];
                updateDraft({
                  devices,
                  exportPreset: EXPORT_PRESET_BY_DEVICE[device] || 'store',
                });
                setActiveDevice(device);
              }}
            />
            <div className="hint">
              Select one device. Export size updates automatically for that device.
            </div>
          </AccordionSection>
          <AccordionSection
            title="Image"
            icon="bi-image"
            isOpen={openSection === 'image'}
            onToggle={() => setOpenSection(openSection === 'image' ? '' : 'image')}
          >
            <UploadImage onChange={setImage} />
            {image && (
              <>
                <p className="file-status">
                  <i className="bi bi-check-circle-fill" /> {image.name}
                </p>
                <label className="field-label">Image fit</label>
                <select
                  className="studio-input"
                  value={draft.cropFit}
                  onChange={(e) => updateDraft({ cropFit: e.target.value })}
                >
                  <option value="contain">Show full image (no crop)</option>
                  <option value="cover">Crop to fill frame</option>
                </select>
                <label className="field-label">
                  Zoom {Number(draft.imageZoom).toFixed(1)}×
                </label>
                <input
                  className="range-control"
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={draft.imageZoom}
                  disabled={draft.cropFit !== 'cover'}
                  onChange={(e) => updateDraft({ imageZoom: Number(e.target.value) })}
                />
                <label className="field-label">Crop horizontal</label>
                <input
                  className="range-control"
                  type="range"
                  min="0"
                  max="100"
                  value={draft.imagePosition.x}
                  disabled={draft.cropFit !== 'cover'}
                  onChange={(e) =>
                    updateDraft({
                      imagePosition: {
                        ...draft.imagePosition,
                        x: Number(e.target.value),
                      },
                    })
                  }
                />
                <label className="field-label">Crop vertical</label>
                <input
                  className="range-control"
                  type="range"
                  min="0"
                  max="100"
                  value={draft.imagePosition.y}
                  disabled={draft.cropFit !== 'cover'}
                  onChange={(e) =>
                    updateDraft({
                      imagePosition: {
                        ...draft.imagePosition,
                        y: Number(e.target.value),
                      },
                    })
                  }
                />
                <button
                  type="button"
                  className="rotate-button"
                  onClick={() =>
                    updateDraft({
                      imageRotation: (Number(draft.imageRotation) + 90) % 360,
                    })
                  }
                >
                  <i className="bi bi-arrow-clockwise" /> Rotate / landscape
                </button>
              </>
            )}
          </AccordionSection>
          <AccordionSection
            title="Text content"
            icon="bi-type"
            isOpen={openSection === 'text'}
            onToggle={() => setOpenSection(openSection === 'text' ? '' : 'text')}
          >
            <label className="field-label">Heading</label>
            <input
              className="studio-input"
              value={draft.heading}
              onChange={(e) => updateDraft({ heading: e.target.value })}
            />
            <div className="text-size-control">
              <label className="field-label">
                Heading size <output>{draft.headingSize || 5}rem</output>
              </label>
              <input
                type="range"
                min="2"
                max="10"
                step="0.1"
                value={draft.headingSize || 5}
                onChange={(e) => updateDraft({ headingSize: Number(e.target.value) })}
              />
            </div>
            <label className="field-label">Subheading</label>
            <textarea
              className="studio-input"
              rows="2"
              value={draft.subheading}
              onChange={(e) => updateDraft({ subheading: e.target.value })}
            />
            <div className="text-size-control">
              <label className="field-label">
                Subheading size <output>{draft.subheadingSize || 3}rem</output>
              </label>
              <input
                type="range"
                min="1"
                max="6"
                step="0.1"
                value={draft.subheadingSize || 3}
                onChange={(e) => updateDraft({ subheadingSize: Number(e.target.value) })}
              />
            </div>
            <ColorPicker
              label="Text colour"
              color={draft.textColor || '#FFFFFF'}
              onChange={(textColor) => updateDraft({ textColor })}
            />
            <label className="field-label">Google Font</label>
            <select
              className="studio-input"
              value={draft.font}
              onChange={(e) => updateDraft({ font: e.target.value })}
            >
              {FONT_OPTIONS.map((font) => (
                <option key={font}>{font}</option>
              ))}
            </select>
          </AccordionSection>
          <AccordionSection
            title="Colours & gradient"
            icon="bi-palette"
            isOpen={openSection === 'colors'}
            onToggle={() => setOpenSection(openSection === 'colors' ? '' : 'colors')}
          >
            <div className="gradient-presets">
              {GRADIENTS.map((gradient) => (
                <button
                  type="button"
                  className="gradient-chip"
                  key={gradient.name}
                  onClick={() =>
                    updateDraft({
                      primary: gradient.primary,
                      secondary: gradient.secondary,
                    })
                  }
                >
                  <span
                    style={{
                      background: `linear-gradient(135deg, ${gradient.primary}, ${gradient.secondary})`,
                    }}
                  />
                  {gradient.name}
                </button>
              ))}
            </div>
            <ColorPicker
              label="Primary"
              color={draft.primary}
              onChange={(primary) => updateDraft({ primary })}
            />
            <ColorPicker
              label="Secondary"
              color={draft.secondary}
              onChange={(secondary) => updateDraft({ secondary })}
            />
            <label className="field-label">Phone frame fill</label>
            <select
              className="studio-input"
              value={draft.frameColor === 'auto' ? 'auto' : 'custom'}
              onChange={(e) =>
                updateDraft({
                  frameColor:
                    e.target.value === 'auto'
                      ? 'auto'
                      : draft.frameColor === 'auto'
                        ? '#FFFFFF'
                        : draft.frameColor,
                })
              }
            >
              <option value="auto">Auto from uploaded image</option>
              <option value="custom">Custom frame colour</option>
            </select>
            {draft.frameColor !== 'auto' && (
              <ColorPicker
                label="Frame colour"
                color={draft.frameColor}
                onChange={(frameColor) => updateDraft({ frameColor })}
              />
            )}
          </AccordionSection>
          <AccordionSection
            title="Layout & effects"
            icon="bi-magic"
            isOpen={openSection === 'layout'}
            onToggle={() => setOpenSection(openSection === 'layout' ? '' : 'layout')}
          >
            <div className="template-grid">
              {TEMPLATES.map((template) => (
                <button
                  type="button"
                  key={template.id}
                  className={`template-card ${draft.layout === template.id ? 'selected' : ''}`}
                  onClick={() => chooseTemplate(template)}
                >
                  <strong>{template.name}</strong>
                  <small>{template.description}</small>
                </button>
              ))}
            </div>
            <label className="field-label">Device effect</label>
            <select
              className="studio-input"
              value={draft.shadow}
              onChange={(e) => updateDraft({ shadow: e.target.value })}
            >
              <option value="none">None</option>
              <option value="shadow">Soft shadow</option>
              <option value="glow">Colour glow</option>
            </select>
            <label className="field-label">Decorations</label>
            <div className="overlay-actions">
              {['★', '→', 'NEW'].map((overlay) => (
                <button
                  type="button"
                  key={overlay}
                  onClick={() => updateDraft({ overlays: [...draft.overlays, overlay] })}
                >
                  {overlay}
                </button>
              ))}
              <button type="button" onClick={() => updateDraft({ overlays: [] })}>
                Clear
              </button>
            </div>
          </AccordionSection>
          <AccordionSection
            title="Export"
            icon="bi-download"
            isOpen={openSection === 'export'}
            onToggle={() => setOpenSection(openSection === 'export' ? '' : 'export')}
          >
            <label className="field-label">Exact export resolution</label>
            <select
              className="studio-input"
              value={draft.exportPreset}
              onChange={(e) => updateDraft({ exportPreset: e.target.value })}
            >
              <option value="store">Native device resolution</option>
              <optgroup label="iPhone App Store">
                <option value="iphone_1242x2688">iPhone 1242 × 2688 px</option>
                <option value="iphone_2688x1242">iPhone 2688 × 1242 px</option>
                <option value="iphone_1284x2778">iPhone 1284 × 2778 px</option>
                <option value="iphone_2778x1284">iPhone 2778 × 1284 px</option>
              </optgroup>
              <optgroup label="iPad 12.9 / 13 inch">
                <option value="ipad_2064x2752">iPad 2064 × 2752 px</option>
                <option value="ipad_2752x2064">iPad 2752 × 2064 px</option>
                <option value="ipad_2048x2732">iPad 2048 × 2732 px</option>
                <option value="ipad_2732x2048">iPad 2732 × 2048 px</option>
              </optgroup>
            </select>
            <p className="hint">
              Choose portrait or landscape. The exported PNG will use the exact selected
              pixel dimensions.
            </p>
          </AccordionSection>
          <button
            type="button"
            className="generate-button"
            disabled={loading}
            onClick={generate}
          >
            <i className={`bi bi-${loading ? 'arrow-repeat spin' : 'sparkles'}`} />
            {loading ? 'Generating preview…' : 'Generate preview'}
          </button>
        </aside>
        <section className="main-panel">
          <div className="canvas-heading">
            <div>
              <span>LIVE PREVIEW</span>
              <h1>{editingPreview ? 'Edit your design' : 'Generated preview'}</h1>
            </div>
            {Object.keys(outputs).length > 0 && (
              <div className="download-actions">
                {!editingPreview && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setEditingPreview(true)}
                  >
                    <i className="bi bi-pencil-square" /> Edit design
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() =>
                    download(outputs[activeDevice], `app-preview-${activeDevice}.png`)
                  }
                >
                  <i className="bi bi-download" /> PNG
                </button>
                <button type="button" className="primary-button" onClick={batchDownload}>
                  <i className="bi bi-file-zip" /> Download ZIP
                </button>
              </div>
            )}
          </div>
          <PreviewCanvas
            outputs={editingPreview ? {} : outputs}
            activeDevice={activeDevice}
            onActiveDevice={setActiveDevice}
            zoom={zoom}
            onZoom={setZoom}
            sourceImage={image}
            draft={draft}
            device={draft.devices[0]}
            onUpdateDraft={updateDraft}
          />
        </section>
      </main>
    </div>
  );
}
export default App;
